import { WorkflowSection } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'

/**
 * WorkflowSection の表示順 (単日 → 週間 → 月間 → 年間) と、
 * 画面別の i18n key を 1 箇所に集約した正本。 backend valueobject.WorkflowSection.SortRank と
 * 順序を一致させる。
 *
 * option 列挙 (フィルタ / 編集フォーム) の全箇所がこの配列から導出するため、
 * 旧当日系の 3 値は読取互換のため enum に残すが、画面ではすべて単日へ正規化する。
 *
 * i18n key は admin / cases で namespace が違うため leaf 共通化せず両方を完全な静的リテラルで持つ
 * (= check-i18n-keys.sh は静的な文字列リテラルの完全キーのみ検証するので、 leaf からの動的生成に
 *  すると断面ラベルの key 存在チェックが効かなくなる)。
 * UNSPECIFIED は「—」表示なのでこの一覧には含めない (= 各画面が個別に fallback する)。
 *
 */
type WorkflowSectionItem = {
  value: WorkflowSection
  adminLabelKey: string
  casesLabelKey: string
}

export const WORKFLOW_SECTION_ITEMS: readonly WorkflowSectionItem[] = [
  {
    value: WorkflowSection.SINGLE_DAY,
    adminLabelKey: 'page:adminWorkflowTemplates.section.singleDay',
    casesLabelKey: 'page:workflowCases.section.singleDay',
  },
  {
    value: WorkflowSection.WEEKLY,
    adminLabelKey: 'page:adminWorkflowTemplates.section.weekly',
    casesLabelKey: 'page:workflowCases.section.weekly',
  },
  {
    value: WorkflowSection.MONTHLY,
    adminLabelKey: 'page:adminWorkflowTemplates.section.monthly',
    casesLabelKey: 'page:workflowCases.section.monthly',
  },
  {
    value: WorkflowSection.ANNUAL,
    adminLabelKey: 'page:adminWorkflowTemplates.section.annual',
    casesLabelKey: 'page:workflowCases.section.annual',
  },
]

export type WorkflowSectionLabelPage =
  | 'adminWorkflowTemplates'
  | 'workflowCases'

/**
 * section → i18n key を解決する。 表示一覧に無い section (UNSPECIFIED / 未知値) は undefined を返し、
 * caller 側で「—」等の fallback に落とす。
 */
export function workflowSectionLabelKey(
  page: WorkflowSectionLabelPage,
  section: WorkflowSection,
): string | undefined {
  const item = WORKFLOW_SECTION_ITEMS.find(
    (s) => s.value === canonicalWorkflowSection(section),
  )
  if (item === undefined) {
    return undefined
  }
  switch (page) {
    case 'adminWorkflowTemplates':
      return item.adminLabelKey
    case 'workflowCases':
      return item.casesLabelKey
  }
}
/** 旧当日系の保存値を、画面で扱う単日の正規値へ寄せる。 */
export function canonicalWorkflowSection(
  section: WorkflowSection,
): WorkflowSection {
  switch (section) {
    case WorkflowSection.TODAY:
    case WorkflowSection.TOMORROW:
    case WorkflowSection.DAY_AFTER_TOMORROW:
      return WorkflowSection.SINGLE_DAY
    default:
      return section
  }
}

/** 雛形編集の計画断面セレクトの 1 選択肢。 */
export type TemplateSectionOption = {
  value: WorkflowSection
  labelKey: string
  /** 公開済み (選べる) か。 false は「この機能は準備中です」で非活性にする */
  selectable: boolean
}

/**
 * 雛形編集の計画断面の選択肢。 表示は常に 4 区分で、 公開済みの断面だけ選べる。
 * 公開済みの集合は backend の GetSectionRelease が正本 (UX の非活性であり、
 * 拒否の正本は backend)。
 */
export function templateSectionOptions(
  released: ReadonlySet<WorkflowSection>,
): TemplateSectionOption[] {
  return WORKFLOW_SECTION_ITEMS.map((item) => ({
    value: item.value,
    labelKey: item.adminLabelKey,
    selectable: released.has(item.value),
  }))
}

/**
 * 既存の雛形の断面を form に入れる値。 旧当日系は単日へ寄せ、 公開されていない断面は
 * 未選択にする (選べない値を form に入れない)。
 */
export function populatableTemplateSection(
  section: WorkflowSection,
  released: ReadonlySet<WorkflowSection>,
): WorkflowSection | undefined {
  const canonical = canonicalWorkflowSection(section)
  return released.has(canonical) ? canonical : undefined
}

/**
 * 「計画を作る時刻」補足文の i18n key (spec AC-WFT-048 の既定: 単日 = 1 日前の 11:00 /
 * 週間 = 2 日前の 09:00)。 断面が未選択の間は単日の文言、 月間・年間は既定が無いので文言なし。
 */
export function planCreationTimeHintKey(
  section: WorkflowSection | undefined,
): string | undefined {
  if (section === undefined) {
    return 'page:adminWorkflowTemplates.subStep.planCreationTimeHint'
  }
  switch (canonicalWorkflowSection(section)) {
    case WorkflowSection.SINGLE_DAY:
      return 'page:adminWorkflowTemplates.subStep.planCreationTimeHint'
    case WorkflowSection.WEEKLY:
      return 'page:adminWorkflowTemplates.subStep.planCreationTimeHintWeekly'
    default:
      return undefined
  }
}
